// ═══════════ Super Admin Module Types ═══════════

// ─── Enums & Constants ──────────────────────────────────────

export enum TenantStatus {
  Active = 0,
  Suspended = 1,
  Expired = 2,
}

export const TenantStatusLabels: Record<number, string> = {
  0: 'Active',
  1: 'Suspended',
  2: 'Expired',
};

export const TenantStatusColors: Record<number, 'success' | 'error' | 'warning'> = {
  0: 'success',
  1: 'error',
  2: 'warning',
};

export enum TicketPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3,
}

export const TicketPriorityLabels: Record<number, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical',
};

export const TicketPriorityColors: Record<number, 'default' | 'info' | 'warning' | 'error'> = {
  0: 'default',
  1: 'info',
  2: 'warning',
  3: 'error',
};

export enum TicketStatus {
  Open = 0,
  InProgress = 1,
  Resolved = 2,
  Closed = 3,
}

export const TicketStatusLabels: Record<number, string> = {
  0: 'Open',
  1: 'In Progress',
  2: 'Resolved',
  3: 'Closed',
};

export const TicketStatusColors: Record<number, 'error' | 'warning' | 'success' | 'default'> = {
  0: 'error',
  1: 'warning',
  2: 'success',
  3: 'default',
};

export enum PaymentStatus {
  Pending = 0,
  Paid = 1,
  Overdue = 2,
  Failed = 3,
}

export enum SubscriptionPeriod {
  Monthly = 1,
  Quarterly = 3,
  HalfYearly = 6,
  Yearly = 12,
}

export const SubscriptionPeriodLabels: Record<number, string> = {
  1: 'Monthly',
  3: 'Quarterly',
  6: 'Half Yearly',
  12: 'Yearly',
};

export const PaymentStatusLabels: Record<number, string> = {
  0: 'Pending',
  1: 'Paid',
  2: 'Overdue',
  3: 'Failed',
};

export const PaymentStatusColors: Record<number, 'warning' | 'success' | 'error' | 'default'> = {
  0: 'warning',
  1: 'success',
  2: 'error',
  3: 'default',
};

export enum NotificationChannel {
  Email = 0,
  InApp = 1,
}

// ─── Super Admin Dashboard ──────────────────────────────────

export interface SuperAdminDashboardData {
  totalTenants: number;
  activeSubscriptions: number;
  expiredSubscriptions: number;
  totalEmployees: number;
  monthlyRevenue: number;
  pendingPayments: number;
  suspendentTenants: number;
  openTickets: number;
  revenueGrowth: RevenueTrend[];
  tenantGrowth: TenantGrowthTrend[];
  planDistribution: PlanDistribution[];
}

export interface RevenueTrend {
  month: string;
  amount: number;
}

export interface TenantGrowthTrend {
  month: string;
  count: number;
}

export interface PlanDistribution {
  planName: string;
  count: number;
}

// ─── Tenant Management ──────────────────────────────────────

export interface TenantDto {
  id: string;
  companyName: string;
  companyCode: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  subscriptionPlanId: string | null;
  subscriptionPlanName: string | null;
  tenantExpiry?: string | null;
  employeeLimit: number;
  currentEmployeeCount: number;
  isActive: boolean;
  isSuspended: boolean;
  createdAt: string;
}

export interface CreateTenant {
  companyName: string;
  companyCode: string;
  email: string;
  phone?: string;
  address?: string;
  country?: string;
  subscriptionPlanId?: string;
  employeeLimit: number;
}

export interface UpdateTenant {
  id: string;
  companyName: string;
  companyCode: string;
  email: string;
  phone?: string;
  address?: string;
  country?: string;
  employeeLimit: number;
}

// ─── Subscription Plan Management ───────────────────────────

export interface SubscriptionPlanDto {
  id: string;
  planName: string;
  pricePerEmployee: number;
  maxUsers: number;
  period?: SubscriptionPeriod;
  isActive: boolean;
  tenantCount: number;
  createdAt: string;
}

export interface CreateSubscriptionPlan {
  planName: string;
  pricePerEmployee: number;
  maxUsers: number;
  period?: SubscriptionPeriod;
}

export interface UpdateSubscriptionPlan {
  id: string;
  planName: string;
  pricePerEmployee: number;
  maxUsers: number;
  period?: SubscriptionPeriod;
  isActive: boolean;
}

// ─── Billing & Invoice Management ───────────────────────────

export interface InvoiceDto {
  id: string;
  tenantId: string;
  tenantName: string;
  invoiceNumber: string;
  planName: string;
  amount: number;
  taxAmount: number | null;
  totalAmount: number;
  paymentStatus: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  paidAt: string | null;
  createdAt: string;
}

export interface GenerateInvoice {
  tenantId: string;
  subscriptionPlanId: string;
  amount: number;
  taxAmount?: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
}

export interface UpdateInvoice {
  id: string;
  amount?: number;
  taxAmount?: number;
  billingPeriodStart?: string;
  subscriptionPlanId?: string;
}

// ─── Support Ticket Management ──────────────────────────────

export interface SupportTicketDto {
  id: string;
  tenantId: string;
  tenantName: string;
  ticketNumber: string;
  subject: string;
  description: string;
  module: string;
  priority: number;
  status: number;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface CreateSupportTicket {
  tenantId: string;
  subject: string;
  description: string;
  module: string;
  priority: number;
}

export interface UpdateSupportTicket extends CreateSupportTicket {
  id: string;
}

export interface UpdateTicketStatus {
  id: string;
  status: number;
}

// ─── Feature Flag Management ────────────────────────────────

export interface FeatureFlagDto {
  id: string;
  featureKey: string;
  isEnabled: boolean;
  planId: string;
  planName: string;
  createdAt: string;
}

export interface CreateFeatureFlag {
  featureKey: string;
  isEnabled: boolean;
  planId: string;
}

export interface UpdateFeatureFlag {
  id: string;
  featureKey: string;
  isEnabled: boolean;
  planId: string;
}

export interface FeatureDefinitionDto {
  featureKey: string;
  displayName: string;
  module: string;
}

export interface TenantFeatureDto extends FeatureDefinitionDto {
  isEnabled: boolean;
}

export interface TenantFeatureSettingsDto {
  tenantId: string;
  tenantName: string;
  features: TenantFeatureDto[];
}

export interface SaveTenantFeatures {
  features: Array<{
    featureKey: string;
    isEnabled: boolean;
  }>;
}

// ─── Role Template Management ───────────────────────────────

export interface RoleTemplateDto {
  id: string;
  roleName: string;
  description: string;
  permissions: RolePermissionItem[];
  isDefault: boolean;
  createdAt: string;
}

export interface RolePermissionItem {
  id: string;
  module: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface CreateRoleTemplate {
  roleName: string;
  description?: string;
  permissions: Omit<RolePermissionItem, 'id'>[];
  isDefault: boolean;
}

export interface UpdateRoleTemplate extends CreateRoleTemplate {
  id: string;
}

// ─── Platform Analytics ─────────────────────────────────────

export interface PlatformAnalyticsDto {
  totalEmployees: number;
  totalAttendanceRecords: number;
  totalLeaveRequests: number;
  totalPayrollRuns: number;
  tenantUsages: TenantUsageSummary[];
}

export interface TenantUsageSummary {
  tenantId: string;
  tenantName: string;
  employeeCount: number;
  attendanceRecords: number;
  leaveRequests: number;
}

// ─── Notification Management ────────────────────────────────

export interface NotificationDto {
  id: string;
  title: string;
  message: string;
  channel: number; // 0=Email, 1=InApp
  targetTenantIds: string[];
  targetTenantNames: string[];
  sentAt: string | null;
  isSent: boolean;
  createdAt: string;
}

export interface CreateNotification {
  title: string;
  message: string;
  channel: number;
  targetTenantIds: string[]; // empty = all tenants
}

// ─── Audit Logs ─────────────────────────────────────────────

export interface AuditLogDto {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  userId: string;
  userName: string;
  tenantId: string | null;
  tenantName: string | null;
  ipAddress: string | null;
  timestamp: string;
}

// ─── Security Monitoring ────────────────────────────────────

export interface SecurityEventDto {
  id: string;
  eventType: string;
  userId: string;
  userName: string;
  tenantId: string | null;
  tenantName: string | null;
  ipAddress: string | null;
  isSuccess: boolean;
  details: string | null;
  timestamp: string;
}

// ─── Usage Monitoring ───────────────────────────────────────

export interface UsageMonitoringDto {
  tenantId: string;
  tenantName: string;
  employeeCount: number;
  employeeLimit: number;
  attendanceRecords: number;
  leaveRequests: number;
  payrollRuns: number;
}

// ─── System Settings ────────────────────────────────────────

export interface SystemSettingsDto {
  id: string;
  settingKey: string;
  settingValue: string;
  description: string | null;
  category: string;
  isEditable: boolean;
  modifiedAt: string;
}

export interface UpdateSystemSetting {
  id: string;
  settingValue: string;
}
